import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { confirmAlert } from "react-confirm-alert";
import { consultarTransacao, remove, save } from "./api-lancamento";
import { BOLETO, PIX, SECRETARIA } from "./FormaPago";
import { findByCurrentUser } from "./HistoricoLancamento/api-historico-lancamento";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import TextFieldDate from "../UI/TextFieldDate";

const FORMAS_PAGO = [SECRETARIA, PIX, BOLETO];

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: "uppercase" }}>
    {children}
  </Typography>
);

const REAIS = { input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> } };
const DATE_FIELD = { textField: { size: "small", fullWidth: true } };

/** Diálogo de cadastro/edição de lançamento financeiro do aluno. */
const LancamentoForm = ({ aluno, lancamento, podeRemover, onClose, onSaved, onRemoved }) => {
  const [form, setForm] = useState(null);
  const [historicos, setHistoricos] = useState([]);
  const [isPerforming, setIsPerforming] = useState(false);

  useEffect(() => {
    findByCurrentUser().then((data) => {
      if (Array.isArray(data)) {
        setHistoricos(data);
      } else if (data?.error) {
        errorMessage(data.message);
      }
    });
  }, []);

  useEffect(() => {
    setForm(lancamento ? { ...lancamento } : null);
  }, [lancamento]);

  const eNovo = !form?.codigo;
  const set = (name, value) => setForm((prevState) => ({ ...prevState, [name]: value }));
  const handleChange = (event) => set(event.target.name, event.target.value);
  const handleSwitch = (name) => (event) => set(name, event.target.checked);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.historicoLancamento) {
      errorMessage("Selecione o histórico do lançamento.");
      return;
    }
    if (!form.dataVencimento) {
      errorMessage("Informe a data de vencimento.");
      return;
    }
    setIsPerforming(true);
    save(form)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível salvar o lançamento.");
          return;
        }
        success("Salvo com sucesso.");
        onSaved(data, eNovo);
        onClose();
      })
      .finally(() => setIsPerforming(false));
  };

  const handleRemove = () =>
    confirmAlert({
      title: "Alerta",
      message: "Deseja remover este lançamento?",
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            setIsPerforming(true);
            remove(form.codigo)
              .then((data) => {
                if (!data || data.error) {
                  errorMessage(data?.message || "Não foi possível remover o lançamento.");
                  return;
                }
                success("Lançamento removido.");
                onRemoved(form.codigo);
                onClose();
              })
              .finally(() => setIsPerforming(false));
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });

  const handleConsultarTransacao = () => {
    setIsPerforming(true);
    consultarTransacao(form.codigo)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível consultar a transação.");
          return;
        }
        onSaved(data, false);
        setForm({ ...data });
        success(data.quitado ? "Pagamento confirmado." : "Transação consultada.");
      })
      .finally(() => setIsPerforming(false));
  };

  return (
    <Dialog open={Boolean(lancamento)} onClose={onClose} fullWidth maxWidth="md">
      {form && (
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {eNovo ? "Novo Lançamento" : "Lançamento"}
            <Typography variant="body2" color="text.secondary">
              {aluno?.nome}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} pt={1}>
              <Stack spacing={2}>
                <SectionTitle>Identificação</SectionTitle>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      size="small"
                      fullWidth
                      required
                      autoFocus={eNovo}
                      label="Ano letivo"
                      name="anoLetivo"
                      value={form.anoLetivo ?? ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      size="small"
                      fullWidth
                      required
                      label="Cód. Doc."
                      name="codDoc"
                      value={form.codDoc ?? ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      required
                      label="Histórico"
                      value={historicos.some((h) => h.codigo === form.historicoLancamento?.codigo) ? form.historicoLancamento.codigo : ""}
                      onChange={(event) =>
                        set("historicoLancamento", historicos.find((h) => h.codigo === event.target.value))
                      }
                    >
                      {historicos.map((h) => (
                        <MenuItem key={h.codigo} value={h.codigo}>
                          {h.descricao}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Stack>

              <Divider />

              <Stack spacing={2}>
                <SectionTitle>Valores</SectionTitle>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextFieldDate
                      label="Vencimento"
                      value={form.dataVencimento ?? null}
                      onChange={(date) => set("dataVencimento", date)}
                      slotProps={{ textField: { ...DATE_FIELD.textField, required: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      size="small"
                      fullWidth
                      required
                      type="number"
                      label="Valor"
                      name="valor"
                      value={form.valor ?? ""}
                      onChange={handleChange}
                      slotProps={REAIS}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextFieldDate
                      label="Desconto até"
                      value={form.dataDesconto ?? null}
                      onChange={(date) => set("dataDesconto", date)}
                      slotProps={DATE_FIELD}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Valor do desconto"
                      name="valorDesconto"
                      value={form.valorDesconto ?? ""}
                      onChange={handleChange}
                      slotProps={REAIS}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Quant. fixa de parcelas"
                      name="quantFixaParcelas"
                      value={form.quantFixaParcelas ?? ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }} display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch checked={Boolean(form.aceitaCartaoCredito)} onChange={handleSwitch("aceitaCartaoCredito")} />
                      }
                      label="Aceita cartão de crédito"
                    />
                  </Grid>
                </Grid>
              </Stack>

              <Divider />

              <Stack spacing={2}>
                <SectionTitle>Pagamento</SectionTitle>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextFieldDate
                      label="Data do pagamento"
                      value={form.dataPagamento ?? null}
                      onChange={(date) => set("dataPagamento", date)}
                      slotProps={DATE_FIELD}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Valor pago"
                      name="valorPago"
                      value={form.valorPago ?? ""}
                      onChange={handleChange}
                      slotProps={REAIS}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Forma de pagamento"
                      name="formaPago"
                      value={form.formaPago ?? ""}
                      onChange={handleChange}
                    >
                      <MenuItem value="">
                        <em>Nenhuma</em>
                      </MenuItem>
                      {FORMAS_PAGO.map((f) => (
                        <MenuItem key={f} value={f}>
                          {f}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Código de barras"
                      name="codigoBarras"
                      value={form.codigoBarras ?? ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={12}>
                    <FormControlLabel
                      control={<Switch checked={Boolean(form.quitado)} onChange={handleSwitch("quitado")} />}
                      label="Quitado"
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
            <Box>
              {!eNovo && podeRemover && (
                <Button color="error" startIcon={<DeleteOutlineIcon />} disabled={isPerforming} onClick={handleRemove}>
                  Remover
                </Button>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              {!eNovo && form.pixTransacao && (
                <Button
                  variant="outlined"
                  startIcon={<ManageSearchIcon />}
                  disabled={isPerforming}
                  onClick={handleConsultarTransacao}
                >
                  Consultar Transação
                </Button>
              )}
              <Button onClick={onClose}>Cancelar</Button>
              <Button type="submit" variant="contained" disabled={isPerforming}>
                Salvar
              </Button>
            </Stack>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default LancamentoForm;
